// Defense-in-depth client-side HTML sanitizer.
//
// The shipping-service already runs agent HTML through a server-side
// allowlist sanitizer (the trust boundary per the API contract). This is a
// SECOND, independent sanitizer applied in the browser before injection so
// that a server-side bypass / regression cannot result in client XSS.
//
// `dompurify` is not a dependency of this project and may not be added over
// the network, so this is a tight, hand-rolled allowlist sanitizer that
// mirrors exactly the tag/attribute allowlist in the API contract:
//
//   block:  h1 h2 h3 h4 p ul ol li blockquote pre code
//           table thead tbody tr th td details summary hr br
//   inline: strong b em i code a
//
// Everything else (script/style/svg/math/iframe/object/embed, on* handlers,
// any non http/https/mailto URL, all unknown tags & attributes) is stripped.
// `a` is forced to rel="noopener noreferrer" and target is dropped.
//
// Implementation notes:
//   - Single linear pass over the input via DOMParser (no regex backtracking,
//     so no ReDoS); the parser itself is O(n).
//   - Output length is capped (MAX_OUTPUT) and tree depth is bounded
//     (MAX_DEPTH) so adversarial deeply-nested / huge input cannot blow up
//     memory or the call stack.
//   - DOMParser('text/html') never executes script and never performs
//     network fetches for element creation, so parsing untrusted markup here
//     is itself side-effect free.

const ALLOWED_TAGS = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "p",
  "ul",
  "ol",
  "li",
  "blockquote",
  "pre",
  "code",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "details",
  "summary",
  "hr",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "a"
]);

// Only `a` carries any attribute through, and only `href`.
const SAFE_URL = /^(https?:|mailto:)/i;

const MAX_OUTPUT = 512 * 1024; // 512 KB rendered cap
const MAX_DEPTH = 64; // bound recursion / nesting

// Drop every ASCII control / space codepoint (<= 0x20). A manual scan
// instead of a regex so there is no control char in source and provably
// no backtracking / ReDoS — strictly O(n) over the string.
function stripControl(s) {
  let result = "";
  for (let i = 0; i < s.length; i++)
    if (s.charCodeAt(i) > 0x20) result += s[i];
  return result;
}

function safeHref(raw) {
  if (typeof raw !== "string") return null;
  // Strip control chars / whitespace that can smuggle `javascript:` past a
  // naive prefix check (e.g. "java\tscript:" or leading newlines).
  const cleaned = stripControl(raw).trim();
  if (!SAFE_URL.test(cleaned)) return null;
  return cleaned;
}

// Elements that are dropped wholesale (their text content is hostile /
// executable and must NOT be preserved).
const DROP_SUBTREE = new Set([
  "script",
  "style",
  "svg",
  "math",
  "iframe",
  "object",
  "embed",
  "template",
  "noscript"
]);

const MAX_NODES = 200000; // hard cap on total nodes processed

function openTag(node, tag) {
  if (tag === "br" || tag === "hr") return `<${tag}>`;
  if (tag === "a") {
    const href = safeHref(node.getAttribute("href"));
    return href
      ? `<a href="${escapeAttr(href)}" rel="noopener noreferrer">`
      : '<a rel="noopener noreferrer">';
  }
  return `<${tag}>`;
}

function pushChildren(node, depth, stack) {
  const kids = node.childNodes;
  for (let i = kids.length - 1; i >= 0; i--)
    stack.push({ node: kids[i], depth: depth + 1 });
}

// Emit one element/text node. Returns true if its children should still be
// walked (the wrapper was either kept or unwrapped), false if the whole
// subtree is dropped or the node produced no descent.
function emitNode(node, depth, out, stack) {
  if (node.nodeType === 3) {
    out.push(escapeText(node.nodeValue || ""));
    return false;
  }
  if (node.nodeType !== 1) return false;

  const tag = node.tagName ? node.tagName.toLowerCase() : "";
  if (DROP_SUBTREE.has(tag)) return false; // element + all descendants

  if (tag === "br" || tag === "hr") {
    if (ALLOWED_TAGS.has(tag) && depth <= MAX_DEPTH)
      out.push(openTag(node, tag));
    return false; // void: never has meaningful children
  }

  if (ALLOWED_TAGS.has(tag) && depth <= MAX_DEPTH) {
    out.push(openTag(node, tag));
    stack.push({ closeOnly: `</${tag}>` });
  }
  // Not allowed (or too deep): unwrap — keep children, drop the wrapper.
  return true;
}

// Iterative, explicit-stack DFS so deeply-nested adversarial markup cannot
// overflow the JS call stack. Depth past MAX_DEPTH is unwrapped (children
// kept, wrapper dropped) so nesting bombs degrade gracefully.
function sanitizeRoot(root, out) {
  // Stack frames are either { node, depth } (a node to process) or
  // { closeOnly } (a sentinel that emits a queued end tag once the node's
  // children have all been processed).
  const stack = [];
  pushChildren(root, -1, stack);

  let processed = 0;
  while (stack.length > 0) {
    if (out.length >= MAX_OUTPUT || processed >= MAX_NODES) return;
    const frame = stack.pop();

    if (frame.closeOnly) {
      out.push(frame.closeOnly);
      continue;
    }

    processed++;
    if (emitNode(frame.node, frame.depth, out, stack))
      pushChildren(frame.node, frame.depth, stack);
  }
}

function escapeText(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Returns sanitized HTML string safe to inject. Always returns a string.
export default function sanitizeHtml(input) {
  if (!input || typeof input !== "string") return "";
  const capped =
    input.length > MAX_OUTPUT ? input.slice(0, MAX_OUTPUT) : input;
  let doc;
  try {
    doc = new DOMParser().parseFromString(capped, "text/html");
  } catch {
    return "";
  }
  if (!doc || !doc.body) return "";
  const out = [];
  sanitizeRoot(doc.body, out);
  let html = out.join("");
  if (html.length > MAX_OUTPUT) html = html.slice(0, MAX_OUTPUT);
  return html;
}
